import React, { useState } from 'react';
import type { Dispatch, SetStateAction, RefObject } from 'react';

// import { validate as uuidValidate } from 'uuid';
import classnames from 'classnames';

import Konva from 'konva';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDown,
  faArrowUp,
  faEye,
  faEyeSlash,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';

import { FlexBox } from '@eltoro-ui/components';

import { capitalize } from '@eltoro-ui/creative_generator/Helpers';

import type { CanvasElement } from 'Components/CanvasEditor/types';

interface LayersProps {
  layerRef: RefObject<Konva.Layer>;
  selectedId: string | null;
  elements: CanvasElement[];
  onSelectElement: (id: string | null) => void;
  onElementChange: (props: Partial<CanvasElement>, id: string, saveToHistory?: boolean) => void;
  setNewElements: Dispatch<SetStateAction<CanvasElement[]>>;
  onDeleteElement: (id: string) => void;
}

export default function Layers({
  layerRef,
  elements,
  setNewElements,
  onSelectElement,
  onDeleteElement,
  onElementChange,
  selectedId,
}: LayersProps) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      const updated = [...elements];
      const [moved] = updated.splice(draggedIndex, 1);
      updated.splice(dragOverIndex, 0, moved);
      setNewElements(updated);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // In reverse order, "up" means increasing index, "down" means decreasing
  const moveElement = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index + 1 : index - 1;

    if (newIndex < 0 || newIndex >= elements.length) return;

    const newElements = [...elements];
    [newElements[index], newElements[newIndex]] = [newElements[newIndex], newElements[index]];
    setNewElements(newElements);
  };

  const onVisibleChange = (element: CanvasElement) => () => {
    onElementChange({ visible: !element.visible }, element.id);
    onSelectElement(element.visible ? null : element.id);
  };

  const onDelete = (element: CanvasElement) => () => {
    onDeleteElement(element.id);
  };

  return (
    <FlexBox flexDirection="column" UNSAFE_className="layers-panel" gap="0.75rem">
      <span className="section-title">Layers</span>
      <div className="layers-list">
        {elements.length === 0 && <span className="no-layers">No elements available</span>}
        {[...elements].reverse().map((element, reverseIndex) => {
          const index = elements.length - 1 - reverseIndex;

          return (
            <div
              key={element.id}
              className={classnames(
                'layer-item',
                selectedId === element.id && 'layer-item-selected',
                draggedIndex === index && 'dragged-layer'
              )}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={e => handleDragOver(e, index)}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
            >
              <div className="layer-content">
                <span className="layer-type">
                  {capitalize(
                    element.type === 'text' && 'background' in element ? 'button' : element.type
                  )}
                </span>
                <div className="layer-actions">
                  <button
                    onClick={() => moveElement(index, 'up')}
                    disabled={index === elements.length - 1}
                    type="button"
                    aria-label="Move up"
                  >
                    <FontAwesomeIcon icon={faArrowUp} />
                  </button>
                  <button
                    onClick={() => moveElement(index, 'down')}
                    disabled={index === 0}
                    type="button"
                    aria-label="Move down"
                  >
                    <FontAwesomeIcon icon={faArrowDown} />
                  </button>
                  <button
                    onClick={onVisibleChange(element)}
                    type="button"
                    aria-label="Toggle visibility"
                  >
                    <FontAwesomeIcon icon={element.visible ? faEye : faEyeSlash} />
                  </button>
                  <button onClick={onDelete(element)} type="button" aria-label="Delete layer">
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </FlexBox>
  );
}
